import type { ApiClient } from '../api/client.js';
import { browserImageCodec, prepareImage, type ImageCodec } from '../media/image-prep.js';
import { confirmUpload, createUploadTickets, putToStorage } from '../media/media-api.js';

/**
 * Pushing the shop's logo through the ordinary media pipeline, as one awaitable call.
 *
 * The same four steps `uploadSketchRendering` takes — prepare, ticket, PUT, confirm — for the
 * same reason: one image, no list of rows, and the caller needs the id back to finish a save.
 *
 * The pipeline always writes JPEG, which is also the only thing the invoice PDF can embed. Most
 * logos are transparent PNGs, and a transparent pixel encoded as JPEG comes out BLACK — so a
 * non-JPEG logo is laid on white first, which is the paper it prints on anyway.
 */

/** The entity the logo is uploaded under. Unattached: the shop is the owner, not a row. */
export const SHOP_LOGO_ENTITY_TYPE = 'shop_logo';

/** A logo does not need a camera's resolution; it prints 64 × 48 points on a bill. */
const LOGO_LONG_EDGE = 800;

async function flattenOntoWhite(file: File): Promise<File> {
  if (file.type === 'image/jpeg') return file;
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.png', { type: 'image/png' });
  } finally {
    bitmap.close();
  }
}

export interface UploadShopLogoInput {
  client: ApiClient;
  file: File;
  /** Test seams, as the sketch upload takes them. */
  codec?: ImageCodec;
  put?: typeof putToStorage;
  flatten?: (file: File) => Promise<File>;
}

/** Upload the logo and return its media id, ready to be saved as the shop's logo. */
export async function uploadShopLogo(input: UploadShopLogoInput): Promise<string> {
  const flat = await (input.flatten ?? flattenOntoWhite)(input.file);
  const prepared = await prepareImage(flat, input.codec ?? browserImageCodec, {
    longEdge: LOGO_LONG_EDGE,
  });

  const [ticket] = await createUploadTickets(input.client, {
    entityType: SHOP_LOGO_ENTITY_TYPE,
    entityId: null,
    files: [
      {
        clientId: 'logo',
        originalFilename: prepared.originalFilename,
        originalBytes: prepared.originalBytes,
        full: {
          bytes: prepared.full.blob.size,
          contentType: prepared.contentType,
          width: prepared.full.width,
          height: prepared.full.height,
        },
        thumb: {
          bytes: prepared.thumb.blob.size,
          contentType: prepared.contentType,
          width: prepared.thumb.width,
          height: prepared.thumb.height,
        },
      },
    ],
  });
  if (!ticket) throw new Error('No upload ticket was issued for the logo');

  const put = input.put ?? putToStorage;
  await put({
    url: ticket.upload.thumb.url,
    body: prepared.thumb.blob,
    headers: ticket.upload.thumb.headers,
  });
  await put({
    url: ticket.upload.full.url,
    body: prepared.full.blob,
    headers: ticket.upload.full.headers,
  });

  const media = await confirmUpload(input.client, ticket.mediaId);
  return media.id;
}
